import { zodResolver } from "@hookform/resolvers/zod";
import { LoaderCircle } from "lucide-react";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useAddLeadSettings } from "@/api/chat-settings/add-lead-settings";
import { useGetWidgetSettings } from "@/api/chat-settings/get-widget-settings";
import { useUpdateLeadSettings } from "@/api/chat-settings/update-lead-settings";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useSearchParams } from "react-router";
import useGlobalPermission from "@/store/user-permissions";

const formSchema = z.object({
    title: z
        .string({
            required_error: "Form title is Required",
            invalid_type_error: "Form title must be a string",
        })
        .min(1, "Form title cannot be empty"),
    button_label: z.string({
        required_error: "Button Label is Required",
        invalid_type_error: "Button Label must be a string",
    }),
    subtitle: z.string({
        required_error: "Subtitle is Required",
        invalid_type_error: "Subtitle must be a string",
    }),
    marketing_consent_text: z.string({
        required_error: "Subtitle is Required",
        invalid_type_error: "Subtitle must be a string",
    }),
    is_marketing_consent: z.boolean(),
    is_collect_phone_number: z.boolean(),
    is_collect_name: z.boolean(),
});

type QuestionType = {
    id: string;
    question: string;
    answer: string;
    is_active: boolean;
};

type Props = {
    question?: QuestionType;
};

const LeadCollectionForm: React.FC<Props> = () => {
    const [open, setOpen] = React.useState<boolean>(false);

    const [searchParams] = useSearchParams();

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            title: "Stay Connected",
            button_label: "Submit",
            subtitle:
                "Please provide your email and phone number to receive personalized updates, exclusive offers, and valuable insights tailored to your interests. We respect your privacy and will never share your information without your consent. You can opt out of our communications anytime. For details on how we handle your data, please see our Privacy Policy.",
            marketing_consent_text: "",
            is_collect_phone_number: false,
            is_collect_name: false,
            is_marketing_consent: false,
        },
    });

    const { mutate: updateLeadSettings, isSuccess: updateSuccess, isPending: updatePending } = useUpdateLeadSettings();

    const { mutate: addLeadSettings, isSuccess: addSuccess, isPending: addPending } = useAddLeadSettings();

    const { data: widgetSettings, isLoading, isError } = useGetWidgetSettings();

    const handleLeadSettingsSubmit = (values: z.infer<typeof formSchema>) => {
        if (!isLoading && !isError && widgetSettings?.data?.lead_collection !== null) {
            updateLeadSettings(values);
        }

        if (!isLoading && !isError && widgetSettings?.data?.lead_collection === null) {
            addLeadSettings(values);
        }
    };

    React.useEffect(() => {
        if (updateSuccess || addSuccess) {
            setOpen(false);
            form.reset();
        }
    }, [form, updateSuccess, addSuccess]);

    useEffect(() => {
        if (searchParams.get("edit_question_id")) {
            setOpen(true);
        }
    }, [searchParams]);

    useEffect(() => {
        if (widgetSettings) {
            form.reset({
                title: widgetSettings?.data?.lead_collection?.title,
                button_label: widgetSettings?.data?.lead_collection?.send_button_label,
                subtitle: widgetSettings?.data?.lead_collection?.subtitle,
                marketing_consent_text: widgetSettings?.data?.lead_collection?.marketing_consent_text,
                is_collect_phone_number: widgetSettings?.data?.lead_collection?.is_collect_phone_number,
                is_collect_name: widgetSettings?.data?.lead_collection?.is_collect_name,
                is_marketing_consent: widgetSettings?.data?.lead_collection?.marketing_consent_status,
            });
        }
    }, [form, widgetSettings]);

    const handleOutsideClick = () => {
        form.reset({
            title: widgetSettings?.data?.lead_collection?.title,
            button_label: widgetSettings?.data?.lead_collection?.send_button_label,
            subtitle: widgetSettings?.data?.lead_collection?.subtitle,
            marketing_consent_text: widgetSettings?.data?.lead_collection?.marketing_consent_text,
            is_collect_phone_number: widgetSettings?.data?.lead_collection?.is_collect_phone_number,
            is_collect_name: widgetSettings?.data?.lead_collection?.is_collect_name,
            is_marketing_consent: widgetSettings?.data?.lead_collection?.marketing_consent_status,
        });
    };

    const { permissionList } = useGlobalPermission();
    const leadsPermission = permissionList.find((item) => item.module.name === "Leads");

    return (
        <>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                    <Button
                        disabled={!leadsPermission?.can_update}
                        className="w-full bg-blue-50 text-lg font-semibold text-gray-600 shadow-none hover:bg-blue-100 md:w-auto"
                    >
                        Customize Lead
                    </Button>
                </DialogTrigger>
                <DialogContent
                    className="sm:max-w-[625px] [&>button]:hidden overflow-y-auto max-h-[calc(100vh-80px)]"
                    onPointerDownOutside={handleOutsideClick}
                    onEscapeKeyDown={handleOutsideClick}
                >
                    <DialogHeader>
                        <DialogTitle>Customize Lead Collection</DialogTitle>
                        <DialogDescription>Customize the lead collection form to match your brand and messaging.</DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-6 py-4">
                        <div className="w-full rounded-lg bg-white">
                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(handleLeadSettingsSubmit)}>
                                    <div className="grid grid-cols-2 gap-2">
                                        <FormField
                                            control={form.control}
                                            name="title"
                                            render={({ field }) => (
                                                <FormItem className="col-span-2 md:col-span-1">
                                                    <FormLabel className="text-base">Form Title*</FormLabel>
                                                    <FormControl>
                                                        {isLoading ? (
                                                            <Skeleton className="h-20 w-full rounded-md bg-gray-200" />
                                                        ) : (
                                                            <Input className="py-6" placeholder="Enter Form Title" {...field} />
                                                        )}
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="button_label"
                                            render={({ field }) => (
                                                <FormItem className="col-span-2 md:col-span-1">
                                                    <FormLabel className="text-base">Send Button Label</FormLabel>
                                                    <FormControl>
                                                        {isLoading ? (
                                                            <Skeleton className="h-20 w-full rounded-md bg-gray-200" />
                                                        ) : (
                                                            <Input className="py-6" placeholder="Enter Button Label" {...field} />
                                                        )}
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="subtitle"
                                            render={({ field }) => (
                                                <FormItem className="col-span-2 md:col-span-1 mt-4">
                                                    <FormLabel className="text-base">Subtitle</FormLabel>
                                                    <FormControl>
                                                        {isLoading ? (
                                                            <Skeleton className="h-20 w-full rounded-md bg-gray-200" />
                                                        ) : (
                                                            <Textarea className="py-1 h-54" placeholder="Enter Subtitle" rows={10} {...field} />
                                                        )}
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <FormField
                                            control={form.control}
                                            name="marketing_consent_text"
                                            render={({ field }) => (
                                                <FormItem className="col-span-2 md:col-span-1 mt-4">
                                                    <FormLabel className="text-base">Marketing Consent Text</FormLabel>
                                                    <FormControl>
                                                        {isLoading ? (
                                                            <Skeleton className="h-20 w-full rounded-md bg-gray-200" />
                                                        ) : (
                                                            <Textarea
                                                                className="py-1 h-54"
                                                                rows={10}
                                                                placeholder="Enter Marketing Consent"
                                                                {...field}
                                                            />
                                                        )}
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="is_collect_phone_number"
                                            render={({ field }) => (
                                                <FormItem className="col-span-2 flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                                                    <div className="space-y-0.5">
                                                        <FormLabel>Collect Phone Number?</FormLabel>
                                                        <FormDescription>Should this form ask for user&apos;s phone number?</FormDescription>
                                                    </div>
                                                    <FormControl>
                                                        {isLoading ? (
                                                            <Skeleton className="h-20 w-full rounded-md bg-gray-200" />
                                                        ) : (
                                                            <Switch checked={field.value} onCheckedChange={field.onChange} aria-readonly />
                                                        )}
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="is_collect_name"
                                            render={({ field }) => (
                                                <FormItem className="col-span-2 flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                                                    <div className="space-y-0.5">
                                                        <FormLabel>Collect User Name?</FormLabel>
                                                        <FormDescription>Should this form have input field for user&apos;s name?</FormDescription>
                                                    </div>
                                                    <FormControl>
                                                        {isLoading ? (
                                                            <Skeleton className="h-20 w-full rounded-md bg-gray-200" />
                                                        ) : (
                                                            <Switch checked={field.value} onCheckedChange={field.onChange} aria-readonly />
                                                        )}
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="is_marketing_consent"
                                            render={({ field }) => (
                                                <FormItem className="col-span-2 flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                                                    <div className="space-y-0.5">
                                                        <FormLabel>Marketing Consent?</FormLabel>
                                                        <FormDescription>Should this form display marketing consent to the user?</FormDescription>
                                                    </div>
                                                    <FormControl>
                                                        {isLoading ? (
                                                            <Skeleton className="h-20 w-full rounded-md bg-gray-200" />
                                                        ) : (
                                                            <Switch checked={field.value} onCheckedChange={field.onChange} aria-readonly />
                                                        )}
                                                    </FormControl>
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                    <div className="col-span-2 mt-4 flex justify-end">
                                        <Button type="submit" disabled={updatePending || addPending}>
                                            {(updatePending || addPending) && <LoaderCircle className="animate-spin" />}
                                            Sav
                                            {updatePending || addPending ? "ing..." : "e"}
                                        </Button>
                                    </div>
                                </form>
                            </Form>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default LeadCollectionForm;
